import {
  Modal,
  ModalBody,
  ModalHeader,
} from 'component_library/2_Molecules/Modal'
import SlotGridHeader from './SlotGridHeader'
import styles from './index.module.scss'

import SlotGridBody from './SlotGridBody'
import { useEffect, useRef, useState } from 'react'
import { Typography } from 'component_library/1_Atoms/Typography'
import { useLeadGenContext } from 'context/LeadGen'
import { Choose, When, Otherwise } from '@/utility_components/ControlStatements'
import { LinkElement } from 'app/ProviderSearch/LeadGenerationElements'
import useBreakpoint from 'hooks/useBreakPoint'
import { cn } from '@/lib/utils'
import usePrimaryBookButtonProfileV2 from 'hooks/treatments/usePrimaryBookButtonProfileV2'

type SlotGridModalProps = {
  showModal: boolean
  onHide: () => void
  providerDetails: Provider
  userSelectedAvailType?: ProviderFilter[]
  groupedFilters?
  showFloralsBehindTheImage?: boolean
  selectedSlot?: SelectedSlot
  appointmentMode?: string
  renderCustomHeader?: () => JSX.Element
  renderCustomBody?: () => JSX.Element
  showProviderCreds?: boolean
  isProfileSurface?: boolean /** Profile page: on mobile, hides the provider header and shows the white fade bar instead. */
  showSlotAndSessionInfo?: boolean
  modalHeadClassOverride?: string
  modalBodyClassOverride?: string
  slotLocation?: string
  lowAvailability?: boolean
}

const SlotGridModal: React.FC<SlotGridModalProps> = ({
  showModal,
  onHide,
  providerDetails,
  userSelectedAvailType,
  groupedFilters,
  showFloralsBehindTheImage,
  selectedSlot,
  appointmentMode = '',
  showProviderCreds = true,
  isProfileSurface = false,
  showSlotAndSessionInfo = false,
  renderCustomHeader,
  renderCustomBody,
  modalHeadClassOverride = '',
  modalBodyClassOverride = '',
  slotLocation,
  lowAvailability = false,
}) => {
  const [showShadow, setShowShadow] = useState<boolean>(false)
  const slotGridBodyRef = useRef<HTMLDivElement>(null)

  // Profile treatment applies only on mobile (≤576px).
  const { isSmallSize } = useBreakpoint()
  const applyProfileMobileTreatment = isProfileSurface && isSmallSize

  const {
    isEnabled: isPrimaryBookButtonV2Enabled,
  } = usePrimaryBookButtonProfileV2()
  const isPrimaryBookButtonMobileEnabled =
    isPrimaryBookButtonV2Enabled && applyProfileMobileTreatment

  const { forwardingFilters, setProviderId } = useLeadGenContext()

  useEffect(() => {
    if (showModal && providerDetails?.id) {
      setProviderId(providerDetails.id)
    }

    return () => {
      setProviderId(undefined)
    }
  }, [showModal, providerDetails?.id, setProviderId])

  useEffect(() => {
    const handleScroll = () => {
      if (
        slotGridBodyRef.current !== null &&
        slotGridBodyRef.current.scrollTop > 1
      ) {
        setShowShadow(true)
      } else {
        setShowShadow(false)
      }
    }

    slotGridBodyRef?.current?.addEventListener('scroll', handleScroll)

    return () => {
      slotGridBodyRef?.current?.removeEventListener('scroll', handleScroll)
    }
  }, [slotGridBodyRef?.current?.scroll])

  return (
    <Modal
      show={showModal}
      onHide={onHide}
      modalContentClass={cn(
        styles.SlotGridModal_content,
        applyProfileMobileTreatment && '!~rounded-t-[32px] !~rounded-b-none'
      )}
      backdropClass={
        applyProfileMobileTreatment ? styles.profile_mobile_backdrop : ''
      }
      backdropOpacity={applyProfileMobileTreatment ? 0.7 : undefined}
    >
      <ModalHeader
        showCloseButton={true}
        showDivider={false}
        headerClassName={cn(
          styles.ModalHeader_container,
          modalHeadClassOverride,
          applyProfileMobileTreatment && '~mb-[18px]'
        )}
        closeButtonClassName={
          applyProfileMobileTreatment ? '!~top-3 [&_svg]:~fill-navy' : ''
        }
      >
        <Choose>
          <When condition={applyProfileMobileTreatment}>
            <div
              aria-hidden="true"
              className="~pointer-events-none ~absolute ~inset-x-0 ~top-0 ~z-[5] ~h-12 ~rounded-t-[32px] ~bg-gradient-to-b ~from-white ~from-[60%] ~to-transparent"
            />
          </When>
          <When condition={!!renderCustomHeader}>{renderCustomHeader?.()}</When>
          <Otherwise>
            <SlotGridHeader
              providerDetails={providerDetails}
              selectedSlot={selectedSlot}
              appointmentMode={appointmentMode}
              showShadow={showShadow}
              showSlotAndSessionInfo={showSlotAndSessionInfo}
              showProviderCreds={showProviderCreds}
              showFloralsBehindTheImage={showFloralsBehindTheImage}
              slotLocation={slotLocation}
            />
          </Otherwise>
        </Choose>
      </ModalHeader>
      <ModalBody
        ref={slotGridBodyRef}
        className={cn(
          styles.ModalBody_container,
          // renderCustomBody skips SlotGridBody (and its PrimaryBookButton
          // footer), so don't reserve footer space when it's in use.
          isPrimaryBookButtonMobileEnabled &&
            !renderCustomBody &&
            styles.ModalBody_container_withPrimaryButton,
          modalBodyClassOverride
        )}
      >
        {renderCustomBody ? (
          renderCustomBody()
        ) : (
          <SlotGridBody
            providerDetails={providerDetails}
            showModal={showModal}
            userSelectedAvailType={userSelectedAvailType}
            groupedFilters={groupedFilters}
            slotLocation={slotLocation}
            isProfileMobile={applyProfileMobileTreatment}
          />
        )}
        {lowAvailability && (
          <div className="~fixed ~bottom-0 ~pb-[46px] ~w-full ~bg-white">
            <Typography
              tag="p"
              family="Gibson"
              className="~mt-5 ~w-full ~text-center"
            >
              Can&#39;t find a time that works?{' '}
              <LinkElement forwardingFilters={forwardingFilters}>
                Get availability alerts
              </LinkElement>
            </Typography>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default SlotGridModal
